import React, { useState, useEffect, useRef } from "react";
import { Clock, CheckCircle, AlertTriangle, BarChart3, ChevronDown, Plus, X, Flag, MoreVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getSportIcon } from '../../utils/events'
import { getBetStatus } from '../../utils/bets'
import {
  SportIcon
} from './BetCard.styled'

const getPct = arb => ((1 - ((1 / arb.bets[0]?.odds) + (1 / arb.bets[1]?.odds))) * 100).toFixed(2);

export const BetCard = ({ bet, setSettledBet, showEmail = false, analyzedEvents = [], onToggleAnalyzedEvent, claimableBetIds = [], onToggleClaimableBet }) => {
  const { t } = useTranslation()
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false)
  const dropdownRef = useRef(null)

  // Check if this event is already being analyzed
  // First check the bet's own analyzedEvent field, then fall back to the analyzedEvents array
  const isEventAnalyzed = bet.analyzedEvent || analyzedEvents.includes(bet.eventId?.toString())

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isDropdownOpen])

  const handleToggleAnalysis = async (e) => {
    e.stopPropagation()
    if (!onToggleAnalyzedEvent || !bet.eventId) return
    
    setIsAnalysisLoading(true)
    try {
      await onToggleAnalyzedEvent(bet.eventId, !isEventAnalyzed)
    } catch (error) {
      // Error is handled in the parent component
    } finally {
      setIsAnalysisLoading(false)
      setIsDropdownOpen(false)
    }
  }

  function BetRow({ bet: individualBet }) {
    const { bookie, name, stake, odds, claimable, id } = individualBet;
    const [isBetDropdownOpen, setIsBetDropdownOpen] = useState(false)
    const [isBetClaimableLoading, setIsBetClaimableLoading] = useState(false)
    const betDropdownRef = useRef(null)

    // Check if this specific bet is claimable
    // First check the bet's own claimable field, then fall back to the claimableBetIds array
    const isBetClaimable = claimable || claimableBetIds.includes(id?.toString())

    // Close bet dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (betDropdownRef.current && !betDropdownRef.current.contains(event.target)) {
          setIsBetDropdownOpen(false)
        }
      }

      if (isBetDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isBetDropdownOpen])

    const handleToggleClaimable = async (e) => {
      e.stopPropagation()
      if (!onToggleClaimableBet || !id) return
      
      setIsBetClaimableLoading(true)
      try {
        await onToggleClaimableBet(id, !isBetClaimable)
      } catch (error) {
        // Error is handled in the parent component
      } finally {
        setIsBetClaimableLoading(false)
        setIsBetDropdownOpen(false)
      }
    }

    return (
      <div className="flex w-full">
        <div className="w-[10px] flex-shrink-0 h-auto rounded-l-lg" style={{ backgroundColor: getBetStatus(individualBet) }}>
        </div>
        <div className="flex items-center justify-between text-sm bg-[#111E12] p-3 rounded-r-lg w-full">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-bold text-white capitalize text-lg">{bookie}</p>
            </div>
            <p className="text-white opacity-70">{name}</p>
          </div>
          <div className="flex items-center space-x-4">
            {isBetClaimable && (
              <div className="flex items-center text-yellow-400 text-sm">
                <AlertTriangle size={16} className="mr-1" />
                <span className="font-medium">{t("claimMe")}</span>
              </div>
            )}
            <div className="text-right">
              <p className="font-bold text-white text-lg">{Number.isInteger(stake) ? stake : stake.toFixed(2)}€</p>
              <p className="text-white opacity-70">Stake</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-white text-lg">{odds.toFixed(2)}</p>
              <p className="text-white opacity-70">{t("oddsIndividual")}</p>
            </div>
            
            {/* Claimable Bet Dropdown */}
            {id && onToggleClaimableBet && (
              <div className="relative" ref={betDropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsBetDropdownOpen(!isBetDropdownOpen)
                  }}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors duration-200 ${
                    isBetClaimable 
                      ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
                      : 'bg-[#2A3C29] hover:bg-[#3A4C39] text-[#ABCDDF]'
                  }`}
                  disabled={isBetClaimableLoading}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {isBetDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-[#2A3C29] rounded-lg shadow-lg z-50">
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-medium text-[#ABCDDF] uppercase tracking-wide border-b border-[#3A4C39] mb-2">
                        {t("manageClaimable")}
                      </div>
                      <button
                        onClick={handleToggleClaimable}
                        disabled={isBetClaimableLoading}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-200 ${
                          isBetClaimable 
                            ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300' 
                            : 'text-yellow-400 hover:bg-yellow-500/10'
                        }`}
                      >
                        {isBetClaimableLoading ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : isBetClaimable ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <Flag className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">
                          {isBetClaimable ? t("removeFromClaimable") : t("markAsClaimable")}
                        </span>
                      </button>
                      {id && (
                        <div className="mt-2 px-3 py-2 text-xs text-[#ABCDDF] bg-[#1B2D1A] rounded border border-[#3A4C39]">
                          Bet ID: {id}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const handleConfirmArbitrage = async (bet) => {
    setIsUpdating(true)
    // Simulate an API call to update the backend
    await setSettledBet(bet);
    setIsConfirmed(true)
    setIsUpdating(false)
  }

  return (
    <div className="w-full bg-[#1B2D1A] shadow-lg rounded-2xl overflow-hidden">
      <div className="bg-[#1B2D1A] text-white p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <SportIcon src={getSportIcon(bet.sportName)} className="mr-2"/>
            <h2 className="text-lg font-semibold">{bet.event ? bet.event.name : bet.eventName}</h2>
          </div>
          <div className="flex items-center gap-2">
            {isEventAnalyzed && (
              <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                <BarChart3 className="w-4 h-4" />
                {t("analyzed")}
              </span>
            )}
            {getPct(bet) > 0 && <span className="bg-[#E8FC8B] text-[#1B2D1A] px-2 py-1 rounded-full text-md font-semibold">{getPct(bet)}%</span>}
            
            {/* Analysis Dropdown */}
            {bet.eventId && onToggleAnalyzedEvent && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsDropdownOpen(!isDropdownOpen)
                  }}
                  className="flex items-center justify-center w-8 h-8 bg-[#2A3C29] hover:bg-[#3A4C39] text-[#E8FC8B] rounded-lg transition-colors duration-200"
                  disabled={isAnalysisLoading}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-[#2A3C29] rounded-lg shadow-lg z-50">
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-medium text-[#ABCDDF] uppercase tracking-wide border-b border-[#3A4C39] mb-2">
                        {t("manageAnalysis")}
                      </div>
                      <button
                        onClick={handleToggleAnalysis}
                        disabled={isAnalysisLoading}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-200 ${
                          isEventAnalyzed 
                            ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300' 
                            : 'text-[#E8FC8B] hover:bg-[#E8FC8B]/10'
                        }`}
                      >
                        {isAnalysisLoading ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : isEventAnalyzed ? (
                          <X className="w-4 h-4" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">
                          {isEventAnalyzed ? t("removeFromAnalysis") : t("analyzeEvent")}
                        </span>
                      </button>
                      {bet.eventId && (
                        <div className="mt-2 px-3 py-2 text-xs text-[#ABCDDF] bg-[#1B2D1A] rounded border border-[#3A4C39]">
                          Event ID: {bet.eventId}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center text-md mt-2 text-[#758b77]">
          <Clock className="w-4 h-4 mr-1" />
          <span> { new Date(bet.timestamp).toLocaleString() }</span>
        </div>
        {showEmail && bet._id?.email && <span className="text-xs text-[#758b77]">{bet._id?.email}</span>}
      </div>
      <div className="p-4 space-y-4">
        {bet.bets.map(individualBet => (
          <BetRow key={individualBet.id} bet={individualBet} />
        ))}
        {new Date().getTime() - new Date(bet.timestamp).getTime() > 24 * 60 * 60 * 1000 && bet.status !== "SETTLED" && !isConfirmed && (
          <button 
            onClick={() => handleConfirmArbitrage(bet)} 
            disabled={isUpdating}
            className="w-full max-w-[230px] ml-auto bg-[#E8FC8B] font-semibold text-[#1B2D1A] hover:bg-[#E8FC7B] transition-colors p-2 rounded-md"
          >
            {isUpdating ? t("updating") : t("confirmArbitrage")}
          </button>
        )}
        {isConfirmed && (
          <div className="flex items-center justify-center font-semibold text-[#1B2D1A] bg-[#E8FC8B] p-2 rounded-md max-w-[230px]">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{t("arbitrageConfirmed")}</span>
          </div>
        )}
      </div>
      {!isNaN(bet.profit) && (
      <div className="bg-[#E8FC8B] p-4 mt-4">
        <div className="w-full flex justify-between items-center text-[#1B2D1A]">
          <span className="font-semibold">{t("totalProfit")}</span>
          <span className="font-bold text-2xl flex items-center">
            {Math.round(bet.profit * 100) / 100}€
          </span>
          </div>
        </div>
      )}
    </div>
  )
}